/**
 * ConnectionBanner — surfaces non-online WebSocket states.
 *
 * The CUI has ~200 fetch sites that no-op while window.__cuiServerAlive is false.
 * Without this banner, a dead WS looks identical to a healthy-but-empty app, and
 * the user's only recovery used to be guessing "maybe I should reload" or hitting
 * a re-login. Fail-loud: show the state, offer the action.
 *
 * Renders nothing when online. Compact pill while reconnecting (informational,
 * non-blocking). Prominent banner with a manual-retry button in the 'dead'
 * state — that's the explicit fail-fast surface from SessionStore.
 */
import { useSessionStore } from '../contexts/SessionStore';

export default function ConnectionBanner() {
  const { connectionState, reconnectAttempts, forceReconnect } = useSessionStore();

  // 'online' = nothing to say. 'connecting' = first-mount, app isn't visible yet either.
  if (connectionState === 'online' || connectionState === 'connecting') return null;

  if (connectionState === 'reconnecting') {
    return (
      <div style={pillStyle} role="status" aria-live="polite">
        <span style={dotStyle} />
        Verbindung verloren — verbinde neu… {reconnectAttempts > 1 ? `(Versuch ${reconnectAttempts})` : ''}
      </div>
    );
  }

  // 'dead' — explicit user action required.
  return (
    <div style={deadBannerStyle} role="alert">
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong>Verbindung zum Server verloren.</strong>
        <span style={{ marginLeft: 8, color: 'var(--tn-text-muted, var(--tn-text-subtle))' }}>
          Auto-Reconnect hat nach mehreren Versuchen aufgegeben.
        </span>
      </div>
      <button data-ai-id="connection-banner-reconnect" onClick={forceReconnect} style={primaryBtnStyle}>
        Neu verbinden
      </button>
      <button data-ai-id="connection-banner-reload-page" onClick={() => window.location.reload()} style={secondaryBtnStyle}>
        Seite neu laden
      </button>
    </div>
  );
}

const pillStyle: React.CSSProperties = {
  position: 'fixed',
  top: 8,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 9999,
  background: 'color-mix(in srgb, var(--tn-surface) 95%, transparent)',
  color: 'var(--tn-text, var(--tn-text))',
  border: '1px solid var(--tn-border, var(--tnf-3b4261))',
  borderRadius: 16,
  padding: '6px 14px',
  fontSize: 13,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  pointerEvents: 'none',
};

const dotStyle: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: 'var(--tn-orange)', // tokyo-night yellow
  animation: 'pulse 1.2s ease-in-out infinite',
  display: 'inline-block',
};

const deadBannerStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 9999,
  background: 'var(--tnfh-7a1f1f)',
  color: '#ffffff',
  padding: '10px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  fontSize: 14,
  borderBottom: '1px solid var(--tnf-5a1414)',
  flexWrap: 'wrap',
};

const primaryBtnStyle: React.CSSProperties = {
  background: '#ffffff',
  color: 'var(--tnf-7a1f1f)',
  border: 'none',
  borderRadius: 4,
  padding: '6px 12px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#ffffff',
  border: '1px solid #ffffff',
  borderRadius: 4,
  padding: '6px 12px',
  fontSize: 13,
  cursor: 'pointer',
};
